import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Download, BookOpen, User, Mail, Loader2 } from 'lucide-react';
import DataProtectionCheckbox from './DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const EBOOK_URL = "https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/dcv498le_ebook_vilaplana_7claves_ley2025.pdf";

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleExitIntent = useCallback((e) => {
    if (e.clientY <= 5 && !sessionStorage.getItem('exit_popup_shown')) {
      setVisible(true);
      sessionStorage.setItem('exit_popup_shown', '1');
    }
  }, []);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('exit_popup_shown');
    if (dismissed) return;
    const timer = setTimeout(() => {
      document.addEventListener('mouseout', handleExitIntent);
    }, 8000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleExitIntent);
    };
  }, [handleExitIntent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads`, {
        nombre: form.nombre,
        email: form.email,
        telefono: '',
        cargo: '',
        empresa: '',
        mensaje: 'Exit-intent popup: eBook 7 Claves Ley 2/2025',
        origen: 'exit_intent_ebook'
      });
      setSubmitted(true);
      window.open(EBOOK_URL, '_blank');
    } catch (err) { /* non-critical: popup lead capture */ }
    setLoading(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="exit-intent-popup">
      <div className="relative bg-white w-full max-w-md mx-4 shadow-2xl animate-fade-in" data-testid="exit-intent-modal">
        <button
          onClick={() => setVisible(false)}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 transition"
          data-testid="exit-intent-close"
        >
          <X size={20} />
        </button>

        <div className="bg-slate-900 p-6 text-center">
          <BookOpen className="mx-auto text-rose-400 mb-3" size={32} />
          <h3 className="text-xl font-bold text-white">Antes de irse...</h3>
          <p className="text-slate-400 text-sm mt-1">Lleve consigo nuestra guia gratuita</p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-4" data-testid="exit-intent-success">
              <Download className="mx-auto text-emerald-600 mb-3" size={36} />
              <p className="font-bold text-slate-900 mb-2">Descarga iniciada</p>
              <a
                href={EBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-rose-600 underline"
              >
                Descargar de nuevo
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="exit-intent-form">
              <p className="text-sm text-slate-600 font-medium mb-4">
                7 Claves Esenciales sobre la Ley 2/2025 — gratis e inmediato.
              </p>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text" required placeholder="Su nombre"
                  value={form.nombre}
                  onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                  data-testid="exit-intent-nombre"
                />
              </div>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email" required placeholder="Su email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                  data-testid="exit-intent-email"
                />
              </div>
              <DataProtectionCheckbox checked={privacyAccepted} onChange={setPrivacyAccepted} id="exit-intent-privacy" />
              <button
                type="submit"
                disabled={loading || !privacyAccepted}
                className="w-full flex items-center justify-center gap-2 py-3 bg-rose-600 text-white font-bold hover:bg-rose-700 transition disabled:opacity-50"
                data-testid="exit-intent-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <><Download size={18} /> Descargar eBook gratis</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
